/**
 * Min-interval request-start scheduler.
 *
 * Enforces a minimum gap between request starts while still allowing the caller
 * to queue one deferred retry callback when invoked too soon.
 *
 * @example
 * // Basic usage around a network request
 * const throttle = createRequestStartThrottle(2000)
 *
 * function attemptRequest() {
 *   if (!throttle.canStartNowOrSchedule(() => attemptRequest())) return
 *   throttle.markStartedNow()
 *   void fetch('/api/example')
 * }
 *
 * @example
 * // Lifecycle cleanup/reset
 * const throttle = createRequestStartThrottle(1500)
 * throttle.clearWait() // cancel pending deferred callback only
 * throttle.reset() // clear callback + forget last start timestamp
 */
export function createRequestStartThrottle(minGapMs: number) {
    let lastStartMs = 0
    let waitTimer: ReturnType<typeof setTimeout> | null = null

    /** Cancel any pending deferred retry callback. */
    function clearWait() {
        if (waitTimer) {
            clearTimeout(waitTimer)
            waitTimer = null
        }
    }

    /** Mark that a request is starting now (call right before network work begins). */
    function markStartedNow() {
        lastStartMs = Date.now()
    }

    /**
     * Returns `true` when work may start immediately.
     * Returns `false` when throttled and schedules one deferred callback after the remaining gap.
     */
    function canStartNowOrSchedule(onAllowedLater: () => void): boolean {
        const elapsed = Date.now() - lastStartMs
        if (elapsed >= minGapMs) {
            return true
        }
        clearWait()
        waitTimer = setTimeout(() => {
            waitTimer = null
            onAllowedLater()
        }, minGapMs - elapsed)
        return false
    }

    /** Reset internal timing state and cancel pending deferred callback. */
    function reset() {
        clearWait()
        lastStartMs = 0
    }

    return {
        clearWait,
        markStartedNow,
        canStartNowOrSchedule,
        reset,
    }
}
